import { mapPixelsToWorld } from './SheetTransforms.js';
import { rotateAround }  from '../Minimap3D/MinimapMath.js';
import SheetMapThumbnail from '../Minimap3D/SheetThumbnail.js';
import { FADE_DURATION, SHEET_OPACITY } from './SheetSyncConstants.js';

const OVERLAY_SCENE = 'thumbnail-overlay-scene';
const EPSILON = 0.0001;

// ThumbnailsOverlay - An overlay of sheet thumbnails.
// For every sheet on the current level, a request for DS will be made in order to get its thumbnail.
// The thumbnail will be displayed as a plane as an overlay on top of the top-view 3D rendering.
//
// In case that the 2D sheet is fully displayed, a (sort of) high-res snapshot of the sheet will be taken,
// and will be used as the thumbnail texture.
export default class ThumbnailsOverlay {
    constructor(viewer, sheetSyncExt) {
        this.viewer = viewer;
        this.sheetSyncExt = sheetSyncExt;

        this.planeGeometry = new THREE.PlaneBufferGeometry(1, 1);

        this.viewer.impl.createOverlayScene(OVERLAY_SCENE);
        this.createThumbnailMesh = this.createThumbnailMesh.bind(this);
    }

    // Create a plane, position it in the correct place in the scene, and assign the thumbnail texture to it.
    createThumbnailMesh(map) {
        if (!this.viewer) return;
        let mesh = map.thumbnail.mesh;

        if (!mesh) {
          const material = new Autodesk.Viewing.Scene.UnlitMaterial({
              opacity: 0.0, // Initially, thumbnail should be hidden.
              transparent: true
          });

          mesh = new THREE.Mesh(this.planeGeometry, material);

          this.setMeshTransform(mesh, map);
          map.thumbnail.mesh = mesh;

          const texture = new THREE.Texture();
          texture.minFilter = THREE.LinearFilter;
          mesh.material.map = texture;
        }

        // Update texture.
        mesh.material.map.image = map.thumbnail.thumbnailImage;
        mesh.material.map.needsUpdate = true;

        this.update();
    }

    setMeshTransform(mesh, map) {
        const minWorld = mapPixelsToWorld(map, map.viewportBounds.min.x, map.viewportBounds.min.y, 0);
        const maxWorld = mapPixelsToWorld(map, map.viewportBounds.max.x, map.viewportBounds.max.y, 0);
        const p2       = mapPixelsToWorld(map, map.viewportBounds.max.x, map.viewportBounds.min.y, 0);

        const angle = Math.atan2(p2.y-minWorld.y, p2.x-minWorld.x);
        const box = new THREE.Box3().setFromPoints([minWorld, maxWorld]);
        const boxCenter = box.getCenter(new THREE.Vector3());
        rotateAround(minWorld, boxCenter, -angle);
        rotateAround(maxWorld, boxCenter, -angle);

        box.setFromPoints([minWorld, maxWorld]);

        const meshSize = box.getSize(new THREE.Vector3());
        const mapSize = map.viewportBounds.getSize(new THREE.Vector3());

        // Height of the plane is camera position minus epsilon, in order to make sure the plane is inside the frustum.
        const center = mapPixelsToWorld(map, mapSize.x / 2, mapSize.y / 2, this.viewer.getCamera().position.z - EPSILON);

        mesh.position.copy(center);
        mesh.rotation.set(0, 0, angle);
        mesh.scale.set(meshSize.x, meshSize.y, 1);
        mesh.dirty = false;
    }

    showThumbnail(thumbnail) {
        if (thumbnail && thumbnail.mesh) {
            if (this.viewer.impl.overlayScenes[OVERLAY_SCENE].scene.children.indexOf(thumbnail.mesh) === -1) {
                this.viewer.impl.addOverlay(OVERLAY_SCENE, thumbnail.mesh);
                this.animateOpacity(thumbnail, 0.5);
            }
        }
    }

    hideThumbnail(thumbnail, immediate) {
        if (thumbnail && thumbnail.mesh) {
            if (this.viewer.impl.overlayScenes[OVERLAY_SCENE].scene.children.indexOf(thumbnail.mesh) !== -1) {
                if (immediate) {
                    this.viewer.impl.removeOverlay(OVERLAY_SCENE, thumbnail.mesh);
                    thumbnail.mesh.material.opacity = 0;
                    thumbnail.mesh.material.uniformsNeedUpdate = true;
                } else {
                    this.animateOpacity(thumbnail, 0.0, () => {
                        this.viewer.impl.removeOverlay(OVERLAY_SCENE, thumbnail.mesh);
                    });
                }
            }
        }
    }

    animateOpacity(thumbnail, targetValue, onDone) {
        if (thumbnail.fadeAnim) {
            if (thumbnail.fadeAnim.targetValue === targetValue) {
                return;
            }

            thumbnail.fadeAnim.stop();
            thumbnail.fadeAnim = null;
        }

        const startVal = thumbnail.mesh.material.opacity;

        const onTimer = (t) => {
            // In case viewer was destroyed during animation.
            if (!this.viewer) {
              return;
            }

            t = Autodesk.Viewing.Private.smootherStep(t);
            const opacity = Autodesk.Viewing.Private.lerp(startVal, targetValue, t);
            this.setThumbnailOpacity(thumbnail, opacity);
        };

        const onFadeEnd = () => {
            thumbnail.fadeAnim = null;
            onDone && onDone();
        };

        thumbnail.fadeAnim = Autodesk.Viewing.Private.fadeValue(0, 1, FADE_DURATION, onTimer, onFadeEnd);
        thumbnail.fadeAnim.targetValue = targetValue;
    }

    setThumbnailOpacity(thumbnail, value) {
        if (thumbnail.mesh.material.opacity !== value) {
            thumbnail.mesh.material.opacity = value;
            thumbnail.mesh.material.uniformsNeedUpdate = true;
            this.viewer.impl.invalidate(false, false, true);
        }
    }

    requestThumbnailDebounce(map) {
      if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
      }

      this.debounceTimeout = setTimeout(() => {
          if (!this.sheetSyncExt) return;

          map.thumbnail = new SheetMapThumbnail(map, this.sheetSyncExt.viewer, this.createThumbnailMesh);

          if (!this.tryRefineThumbnail(map)) {
              map.thumbnail.requestThumbnail();
          }
      }, 300);
    }

    tryRefineThumbnail(map) {
        if (this.sheetSyncExt.isSheetMapLoaded(map) &&
            this.sheetSyncExt.viewer.model.isLoadDone(true) &&
            this.sheetSyncExt.viewer.model.opacity === SHEET_OPACITY) {

            map.thumbnail.refineThumbnail();
            return true;
        }

        return false;
    }

    // Verify all the thumbnails are displayed correctly.
    update() {
        const sheetMaps = this.sheetSyncExt.mapCache;
        const currentLevel = this.sheetSyncExt.getCurrentLevel();

        if (!currentLevel) {
            return;
        }

        const showSheetEnabled = this.sheetSyncExt.getShowSheet();

        Object.keys(sheetMaps).forEach((levelGuid) => {
            const levelMaps = sheetMaps[levelGuid];
            const sameLevel = levelGuid === currentLevel.guid;
            levelMaps.forEach(map => {
                const showThumbnail = sameLevel && showSheetEnabled && !this.sheetSyncExt.isCameraInsideMap(map);

                if (sameLevel) {
                    if (!map.thumbnail) {
                        // Thumbnail was not fetched yet - initialize thumbnail object and request thumbnail image.
                        // Update when loaded.
                        this.requestThumbnailDebounce(map);
                    } else if (!map.thumbnail.thumbnailRefined) {
                        this.tryRefineThumbnail(map);
                    }
                }

                // Update transform if model transform was changed.
                if (map.thumbnail?.mesh?.dirty) {
                    this.setMeshTransform(map.thumbnail.mesh, map);
                }

                if (showThumbnail) {
                    // If thumbnail mesh is ready - add it to the overlay scene if it's not there already.
                    this.showThumbnail(map.thumbnail);
                } else {
                    // Thumbnail mesh is ready, but should be hidden because it's not on the current level -
                    // Remove it from the overlay scene if it's there.
                    this.hideThumbnail(map.thumbnail, levelGuid !== currentLevel.guid);
                }
            });
        });
    }

    destroy() {
        this.planeGeometry.dispose();
        this.planeGeometry = null;

        this.viewer.impl.removeOverlayScene(OVERLAY_SCENE);

        this.sheetSyncExt = null;
        this.viewer = null;
    }
}
